import React from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import { Spanish } from "flatpickr/dist/l10n/es.js";

/**
 * Componentes de formulario nativos (input / select / button / date) que
 * reemplazan a los de mdbreact (MDBInput / MDBSelect / MDBBtn / MDBDatePicker).
 *
 * Mantienen LA MISMA API que los componentes MDB para que el cambio en los
 * formularios sea solo renombrar la etiqueta, sin tocar los handlers:
 *   - XSelect.getValue(arrayDeValores)  <- igual que MDBSelect
 *   - XInput onChange(event)            <- event.target.{name,id,value}
 *   - XBtn color/size/outline/href      <- clases bootstrap ya estilizadas
 *   - XDatePicker.getValue("DD/MM/YYYY")
 *
 * Heredan el modo noche existente porque usan input/select/textarea nativos,
 * que ya están cubiertos por las reglas `.xflow-external-dark` de App.css.
 */

const sizeClass = (size) => {
  if (size === "sm" || size === "xs") return " form-control-sm";
  if (size === "lg") return " form-control-lg";
  return "";
};

/* ── Input / textarea ──────────────────────────────────────────────────── */
export const XInput = (props) => {
  const {
    label,
    type = "text",
    rows,
    size,
    className = "",
    containerClass = "",
    style,
    hint, // ignorado (compat MDB)
    icon, // ignorado (compat MDB)
    background, // ignorado (compat MDB)
    outline, // ignorado (compat MDB)
    getValue, // compat MDB: callback con el valor
    onChange,
    ...rest
  } = props;

  if (type === "hidden") {
    return <input type="hidden" {...rest} />;
  }

  const handleChange = (e) => {
    if (onChange) onChange(e);
    if (getValue) getValue(e.target.value);
  };

  const controlClass = `form-control xui-control${sizeClass(size)} ${className}`.trim();
  const isTextarea = type === "textarea" || rows;

  const control = isTextarea ? (
    <textarea
      className={controlClass}
      rows={rows || 3}
      style={style}
      onChange={handleChange}
      {...rest}
    />
  ) : (
    <input
      type={type}
      className={controlClass}
      style={style}
      onChange={handleChange}
      {...rest}
    />
  );

  return (
    <div className={`xui-field ${containerClass}`.trim()}>
      {label ? <label htmlFor={rest.id}>{label}</label> : null}
      {control}
    </div>
  );
};

/* ── Select ────────────────────────────────────────────────────────────── */
export class XSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = { value: this.initialValue(props.options, props.multiple) };
  }

  initialValue(options, multiple) {
    const opts = options || [];
    const checked = opts.filter((o) => o && o.checked);
    if (multiple) return checked.map((o) => String(o.value));
    return checked.length ? String(checked[0].value) : "";
  }

  componentDidMount() {
    // Paridad con MDBSelect: si hay opción(es) pre-marcadas, emite el valor.
    const { options, getValue } = this.props;
    const checked = (options || []).filter((o) => o && o.checked);
    if (checked.length && getValue) {
      getValue(checked.map((o) => o.value));
    }
  }

  componentDidUpdate(prevProps) {
    // Si las opciones llegan luego (async) y traen checked, sincroniza.
    if (prevProps.options !== this.props.options) {
      const next = this.initialValue(this.props.options, this.props.multiple);
      if (next && next !== this.state.value) this.setState({ value: next });
    }
  }

  handleChange = (e) => {
    const { multiple, getValue, onChange } = this.props;
    let value;
    let payload;
    if (multiple) {
      value = Array.from(e.target.selectedOptions).map((o) => o.value);
      payload = value;
    } else {
      value = e.target.value;
      payload = value === "" ? [] : [value];
    }
    this.setState({ value });
    if (getValue) getValue(payload);
    if (onChange) onChange(e);
  };

  render() {
    const {
      label,
      options = [],
      size,
      className = "",
      containerClass = "",
      style,
      multiple,
      placeholder,
      selected, // texto placeholder (compat MDB)
      id,
      name,
      disabled,
      color, // ignorado (compat MDB)
      search, // ignorado (compat MDB)
      getValue, // consumido arriba
      onChange, // consumido en handleChange
      getTextContent, // ignorado (compat MDB)
      ...rest
    } = this.props;

    const placeholderText = placeholder || selected || "Seleccione...";

    return (
      <div className={`xui-field ${containerClass}`.trim()}>
        {label ? <label htmlFor={id}>{label}</label> : null}
        <select
          id={id}
          name={name}
          disabled={disabled}
          multiple={multiple}
          className={`form-control xui-control xui-select${sizeClass(size)} ${className}`.trim()}
          style={style}
          value={this.state.value}
          onChange={this.handleChange}
          {...rest}
        >
          {!multiple ? (
            <option value="">{placeholderText}</option>
          ) : null}
          {options.map((o, i) => (
            <option
              key={(o && o.value != null ? o.value : "opt") + "_" + i}
              value={o.value}
              disabled={o.disabled}
            >
              {o.text}
            </option>
          ))}
        </select>
      </div>
    );
  }
}

/* ── Button ────────────────────────────────────────────────────────────── */
export const XBtn = (props) => {
  const {
    color = "primary",
    size,
    outline,
    block,
    rounded, // ignorado (compat MDB)
    gradient, // ignorado (compat MDB)
    href,
    type,
    className = "",
    children,
    ...rest
  } = props;

  const cls = [
    "btn",
    "xui-btn",
    outline ? `btn-outline-${color}` : `btn-${color}`,
    size === "sm" || size === "xs" ? "btn-sm" : "",
    size === "lg" ? "btn-lg" : "",
    block ? "btn-block" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (href) {
    return (
      <a href={href} className={cls} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <button type={type || "button"} className={cls} {...rest}>
      {children}
    </button>
  );
};

/* ── DatePicker ────────────────────────────────────────────────────────── */
const pad2 = (n) => String(n).padStart(2, "0");

// Convierte Date | "DD/MM/YYYY" | "YYYY-MM-DD" a valor de <input type=date> (YYYY-MM-DD)
const toInputDate = (value) => {
  if (!value) return "";
  if (value instanceof Date && !isNaN(value)) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
  }
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(str);
  if (!isNaN(d)) return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  return "";
};

// YYYY-MM-DD -> DD/MM/YYYY (formato que emitía MDBDatePicker con format="DD/MM/YYYY")
const toDdMmYyyy = (inputDate) => {
  const m = String(inputDate).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : inputDate;
};

// value (Date | "DD/MM/YYYY" | "YYYY-MM-DD") -> Date para flatpickr
const toDateObj = (value) => {
  const iso = toInputDate(value);
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/* Calendario lindo con flatpickr. Mantiene API de MDBDatePicker:
   value, getValue("DD/MM/YYYY"), label. */
export class XDatePicker extends React.Component {
  handleChange = (dates, dateStr) => {
    if (this.props.getValue) this.props.getValue(dateStr || toDdMmYyyy(toInputDate(dates && dates[0])));
  };

  _applyDarkToCalendar = (instance) => {
    if (typeof document === "undefined" || !instance || !instance.calendarContainer) return;
    const isDark = document.body.classList.contains("xflow-external-dark")
      || document.documentElement.classList.contains("xflow-external-dark");
    const cal = instance.calendarContainer;
    cal.classList.toggle("xflow-dark-fp", isDark);
    // Inline styles como fallback por si la especificidad CSS pierde contra flatpickr
    if (isDark) {
      cal.style.background = "#182235";
      cal.style.border = "1px solid #334155";
      cal.style.color = "#e2e8f0";
      cal.style.boxShadow = "0 12px 32px rgba(0,0,0,0.5)";
      // Contenedores internos del grid
      [".flatpickr-innerContainer", ".flatpickr-rContainer", ".flatpickr-days", ".dayContainer"].forEach((sel) => {
        cal.querySelectorAll(sel).forEach((el) => { el.style.background = "#182235"; });
      });
      // Días
      cal.querySelectorAll(".flatpickr-day").forEach((d) => { d.style.color = "#e2e8f0"; });
      // Header meses y días semana
      [".flatpickr-months", ".flatpickr-month", ".flatpickr-weekdays", ".flatpickr-weekday"].forEach((sel) => {
        cal.querySelectorAll(sel).forEach((el) => {
          el.style.background = "#182235";
          el.style.color = "#f8fafc";
        });
      });
      // Dropdown mes
      cal.querySelectorAll(".flatpickr-monthDropdown-months").forEach((el) => {
        el.style.background = "#182235";
        el.style.color = "#f8fafc";
      });
    } else {
      cal.style.background = "";
      cal.style.border = "";
      cal.style.color = "";
      cal.style.boxShadow = "";
    }
  };

  handleReady = (_dates, _str, instance) => {
    this._applyDarkToCalendar(instance);
    if (this.props.onReady) this.props.onReady(_dates, _str, instance);
  };

  handleOpen = (_dates, _str, instance) => {
    this._applyDarkToCalendar(instance);
  };

  render() {
    const {
      label,
      size,
      className = "",
      containerClass = "",
      style,
      id,
      name,
      disabled,
      // props MDB ignoradas
      format,
      value,
      getValue,
      cancelLabel,
      clearLabel,
      okLabel,
      keepValue,
      autoOk,
      onReady,
      ...rest
    } = this.props;

    return (
      <div className={`xui-field ${containerClass}`.trim()}>
        {label ? <label htmlFor={id}>{label}</label> : null}
        <Flatpickr
          id={id}
          name={name}
          disabled={disabled}
          className={`form-control xui-control xui-date${sizeClass(size)} ${className}`.trim()}
          style={style}
          value={toDateObj(value)}
          onChange={this.handleChange}
          options={{
            dateFormat: "d/m/Y",
            allowInput: true,
            disableMobile: true,
            locale: Spanish,
            onReady: this.handleReady,
            onOpen: this.handleOpen,
          }}
          {...rest}
        />
      </div>
    );
  }
}

export default { XInput, XSelect, XBtn, XDatePicker };
